import { useSyncExternalStore } from "react";
import { designV2Override, subscribeDesignV2Override } from "../hooks/configuration";

/**
 * Design v2 on the web: this browser's explicit choice (`designV2Override`), else the admitted
 * project's `design_v2` flag, else classic.
 *
 * The flag is read inside the tenant query client, which mounts after admission, while the
 * presentation is chosen above admission (`RedesignProvider`) so a design switch remounts neither.
 * `DesignV2Gate` holds the project's pages until the flag's first read ends and decides the design
 * here. Until then, unless this browser chose, the design is undecided and the shell draws neither
 * (`BootstrapShell`): no page mounts in a design the project does not use, only to be torn down and
 * mounted again in the other.
 */

const listeners = new Set<() => void>();
/** The admitted project's design, from its flag's first read; null until that read ends, and
 *  once the project is gone. */
let decided: boolean | null = null;

function set(next: boolean | null): void {
    if (decided === next) return;
    decided = next;
    listeners.forEach((listener) => listener());
}

/**
 * The admitted project's design, from the first read of its flag. A later value (a retry, an
 * admin turning the flag over mid-session) waits for the next load, because switching the design
 * remounts the page and would drop unsaved work.
 */
export function decideTenantDesignV2(value: boolean): void {
    if (decided === null) set(value);
}

/** The admitted project is gone (sign-out, a switch): the next one decides again. */
export function leaveTenantDesignV2(): void {
    set(null);
}

/** Whether Design v2 is on now. */
export function designV2Enabled(): boolean {
    return designV2Override() ?? decided ?? false;
}

/** Whether the design is known: this browser chose one, or the admitted project's flag decided. */
export function designV2Decided(): boolean {
    return designV2Override() !== null || decided !== null;
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    const unsubscribeOverride = subscribeDesignV2Override(listener);
    return () => {
        listeners.delete(listener);
        unsubscribeOverride();
    };
}

export function useDesignV2(): boolean {
    return useSyncExternalStore(subscribe, designV2Enabled, () => false);
}

export function useDesignV2Decided(): boolean {
    return useSyncExternalStore(subscribe, designV2Decided, () => false);
}
